import { Gift, SprayCan, AlertTriangle } from "lucide-react"
import { DS } from "../lib/ui"
import { prixUnitePrelevable } from "../lib/depots-helpers"

// DepotCountersList — ADR-210.
// Composant PRÉSENTATIONNEL PUR extrait de SaisirDepotModal (bloc « Liste produits »,
// sections Kit/Opérationnel + compteurs +/- + warning + total-ligne). Contrôlé :
// l'état `lignes`/`warn`, la borne `maxFor` et le clamp `onSetQty` vivent chez le
// caller (SaisirDepotModal = édition avec relax ; raccourci checklist = création).
// Rendu BYTE-IDENTIQUE à l'original (mêmes styles inline, ordre kit→oper, sections
// vides filtrées). Ajoute `disabled` (raccourci verrouillé >48h) — false côté Stock.
//
// Props :
//   kitProduits, operProduits : [{id, nom, qtyParAchat, qtyParPrelevable, prixAchat, formatAchat}]
//   stockCentral  : { [csId]: { qtyDispo, seuilMin } }
//   lignes        : { [csId]: nbPrelevables }   (source de vérité = caller)
//   warn          : { [csId]: boolean }          (clignotement « stock insuffisant »)
//   maxFor(csId)  : number                       (borne ; diffère édition vs création)
//   onSetQty(csId, n) : void                     (le caller applique clamp + warn)
//   originalLignes, isEdit : affichage « (+ N actuels) » en mode édition
//   disabled      : bool — désactive les ± et l'input (défaut false)
//   hidePrices    : bool — masque prix unitaire + total-ligne (compta) côté
//                   ménage (raccourci checklist). Défaut false → admin inchangé.
export default function DepotCountersList({
  kitProduits = [], operProduits = [],
  stockCentral = {}, lignes = {}, warn = {},
  maxFor, onSetQty,
  originalLignes = {}, isEdit = false,
  disabled = false, hidePrices = false,
}) {
  return (
    <>
      {[
        { titre: "Kit d'accueil", ico: Gift, prods: kitProduits },
        { titre: "Opérationnel", ico: SprayCan, prods: operProduits },
      ].filter(s => s.prods.length > 0).map(section => (
        <div key={section.titre} style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:5,fontSize:10,fontWeight:700,color:DS.textMuted,textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}><section.ico size={12} strokeWidth={2}/>{section.titre}</div>
          {section.prods.map(p => {
            const qty = lignes[p.id] || 0
            const dispo = stockCentral?.[p.id]?.qtyDispo || 0
            const max = maxFor(p.id)
            const prixU = prixUnitePrelevable(p)
            const totalLigne = qty * prixU
            const labelUnite = labelUnitePrelevable(p)
            const insuffisant = !!warn[p.id]
            const epuise = max === 0
            const lock = disabled || epuise
            return (
              <div key={p.id} style={{padding:"10px 0",borderBottom:`1px solid ${DS.border}`,opacity: lock ? .55 : 1}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:DS.marine}}>{p.nom}</div>
                    <div style={{fontSize:10,color:DS.textMuted,marginTop:2}}>
                      Stock dispo : <span style={{fontWeight:700,color:dispo>0?DS.marine:DS.danger}}>{dispo} {pluriel(labelUnite, dispo)}</span>
                      {isEdit && originalLignes[p.id] > 0 && (
                        <span style={{color:DS.textMuted,marginLeft:6}}>(+ {originalLignes[p.id]} actuels)</span>
                      )}
                      {!hidePrices && (
                        <span style={{marginLeft:6}}>· {prixU.toFixed(3)} €/{labelUnite}</span>
                      )}
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <button className="fbz-press" onClick={()=>onSetQty(p.id, qty-1)} disabled={qty<=0 || lock}
                      style={{width:28,height:28,borderRadius:8,background:qty>0?DS.cielSoft:DS.pageBg,color:qty>0?DS.marine:DS.textMuted,border:`0.5px solid ${DS.borderHover}`,fontWeight:700,fontSize:14,cursor:qty>0&&!lock?"pointer":"not-allowed"}}>−</button>
                    <input type="number" min="0" max={max} value={qty} disabled={lock}
                      onChange={e=>onSetQty(p.id, e.target.value)}
                      style={{width:54,textAlign:"center",border:`0.5px solid ${insuffisant?DS.warning:DS.borderHover}`,borderRadius:7,padding:"4px 6px",fontSize:13,fontWeight:700,fontFamily:DS.fontBody,outline:"none",background:insuffisant?DS.warningBg:DS.card}}/>
                    <button className="fbz-press" onClick={()=>onSetQty(p.id, qty+1)} disabled={qty>=max || lock}
                      style={{width:28,height:28,borderRadius:8,background:qty<max && !lock?DS.marine:DS.border,color:qty<max && !lock?"#fff":DS.textMuted,border:"none",fontWeight:700,fontSize:14,cursor:qty<max && !lock?"pointer":"not-allowed"}}>+</button>
                  </div>
                </div>
                {insuffisant && (
                  <div style={{display:"flex",alignItems:"center",gap:4,marginTop:4,fontSize:10,color:DS.warningText,fontWeight:700}}><AlertTriangle size={12} strokeWidth={2.2}/>Stock insuffisant — quantité limitée à {max}</div>
                )}
                {!hidePrices && qty > 0 && (
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:11}}>
                    <span style={{color:DS.textSecondary}}>{qty} {pluriel(labelUnite, qty)}</span>
                    <span style={{fontWeight:700,color:DS.marine}}>{totalLigne.toFixed(2)} €</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}

// ⚠️ PAS DE PLURIEL SUR UN CHIFFRE (« lot de 1s », vu le 2026-08-23) : on
// n'ajoute le « s » que si le libellé finit par une lettre.
function pluriel(mot, n) {
  const m = String(mot || '')
  if (n <= 1) return m
  return /[0-9)]$/.test(m) ? m : `${m}s`
}

// Libellé d'une unité prélevable, lisible côté UI (extrait de SaisirDepotModal).
// ⚠️ LE MOT DU TERRAIN, PAS CELUI DE LA GESTION (Dimitri 2026-08-23) :
// « sous-paquet de 4 » ne veut rien dire pour un agent qui prend des dosettes.
// On nomme la CHOSE quand le produit la porte (`uniteTerrain` / `formatAchat`),
// et on ne retombe sur « paquet » qu'à défaut de mieux.
function labelUnitePrelevable(p) {
  const qtyA = p?.qtyParAchat || 1
  const qtyP = p?.qtyParPrelevable ?? qtyA
  const mot = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null)
  const nomme = mot(p?.uniteTerrain) || mot(p?.unitePrelevable)
  if (nomme) return nomme
  if (qtyA === qtyP) return mot(p?.formatAchat) || 'paquet'
  // ⚠️ « lot de 1 » ne veut rien dire (régression vue le 2026-08-23) : quand
  // l'unité prélevable vaut 1, c'est simplement une PIÈCE.
  return qtyP > 1 ? `lot de ${qtyP}` : 'pièce'
}
